/**
 * TrpcFeedbackSubmitAdapter — the production `FeedbackSubmitPort` over the
 * typed tRPC client (033 US5, T040). Calls the live
 * `member.feedback.submit`: the member's cookie session authenticates the
 * same-origin request and `memberProcedure` on the API side derives the
 * submitter's pseudonym from the session, never from the payload.
 *
 * The `member` namespace is OPTIONAL on the published `AppRouter` (the
 * conditional-spread posture), so the adapter narrows before calling and fails
 * loudly if wired against a router without the namespace (a composition error,
 * not a runtime condition — loud > silent, P7). Mirrors
 * `TrpcAccountDeletionAdapter`.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type { FeedbackSubmission, FeedbackSubmitPort } from './FeedbackSubmitPort.js';

export class TrpcFeedbackSubmitAdapter implements FeedbackSubmitPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async submit(submission: FeedbackSubmission): Promise<void> {
    const member = this.#client.member;
    if (member?.feedback === undefined) {
      throw new Error(
        'TrpcFeedbackSubmitAdapter: member.feedback is absent on the AppRouter — ' +
          'feedback submission requires the 033 US5 API activation.',
      );
    }
    // Omit rather than send `undefined`: the API's zod schema treats an absent
    // field and a present-but-undefined field the same, but the wire payload
    // stays minimal and the "no content on this axis" intent is explicit.
    await member.feedback.submit.mutate({
      ...(submission.rating === undefined ? {} : { rating: submission.rating }),
      ...(submission.text === undefined || submission.text.length === 0
        ? {}
        : { text: submission.text }),
    });
  }
}
